<template>
    <portal to="modals">
        <div v-if="isOpen && appointment" class="availability-appointment-modal__overlay" @click.self="closeModal">
            <div class="availability-appointment-modal" role="dialog" aria-modal="true" :aria-label="patientName">
                <button class="availability-appointment-modal__close" type="button" @click="closeModal">
                    <common-icon class="availability-appointment-modal__close-icon" name="close" size="tiny"></common-icon>
                </button>

                <div class="availability-appointment-modal__header">
                    <div class="availability-appointment-modal__identity">
                        <div class="availability-appointment-modal__avatar">{{ patientInitials }}</div>

                        <div class="availability-appointment-modal__identity-copy">
                            <h2 class="availability-appointment-modal__patient-name">
                                {{ patientName }}
                            </h2>
                            <p class="availability-appointment-modal__patient-age">
                                {{ ageText }}
                            </p>
                        </div>
                    </div>
                </div>

                <div class="availability-appointment-modal__divider"></div>

                <div v-if="isLoading" class="availability-appointment-modal__loading">
                    <span class="availability-appointment-modal__spinner"></span>
                </div>

                <div class="availability-appointment-modal__content">
                    <section class="availability-appointment-modal__section availability-appointment-modal__section--details">
                        <div class="availability-appointment-modal__section-header">
                            <h3>{{ $t('pages.availability.index.modal.appointment_details') }}</h3>
                            <div :class="appointmentStatusClass">
                                {{ appointmentStatusLabel }}
                            </div>
                        </div>

                        <div class="availability-appointment-modal__detail-grid">
                            <div class="availability-appointment-modal__detail-item">
                                <common-icon name="calendar" size="small"></common-icon>
                                <div class="availability-appointment-modal__detail-copy">
                                    <span class="availability-appointment-modal__sr-only">
                                        {{ $t('pages.availability.index.modal.appointment_date') }}
                                    </span>
                                    <p>{{ appointmentDate }}</p>
                                </div>
                            </div>

                            <div class="availability-appointment-modal__detail-item">
                                <common-icon name="timer" size="small"></common-icon>
                                <div class="availability-appointment-modal__detail-copy">
                                    <span class="availability-appointment-modal__sr-only">
                                        {{ $t('pages.availability.index.modal.appointment_time') }}
                                    </span>
                                    <p>{{ appointmentTime }}</p>
                                </div>
                            </div>

                            <div
                                class="availability-appointment-modal__detail-item availability-appointment-modal__detail-item--wide"
                            >
                                <common-icon name="file" size="small"></common-icon>
                                <div class="availability-appointment-modal__detail-copy">
                                    <span class="availability-appointment-modal__sr-only">
                                        {{ $t('pages.availability.index.modal.appointment_type') }}
                                    </span>
                                    <p>{{ appointmentType }}</p>
                                </div>
                            </div>
                        </div>
                    </section>

                    <section class="availability-appointment-modal__section availability-appointment-modal__section--flat">
                        <div class="availability-appointment-modal__section-header">
                            <h3>{{ $t('pages.availability.index.modal.contact_information') }}</h3>
                        </div>

                        <div class="availability-appointment-modal__list">
                            <div class="availability-appointment-modal__list-item">
                                <common-icon name="phone" size="small"></common-icon>
                                <span class="availability-appointment-modal__sr-only">
                                    {{ $t('pages.availability.index.modal.phone') }}
                                </span>
                                <p>{{ phone }}</p>
                            </div>

                            <div class="availability-appointment-modal__list-item">
                                <common-icon name="email" size="small"></common-icon>
                                <span class="availability-appointment-modal__sr-only">
                                    {{ $t('pages.availability.index.modal.email') }}
                                </span>
                                <p>{{ email }}</p>
                            </div>

                            <div
                                class="availability-appointment-modal__list-item availability-appointment-modal__list-item--address"
                            >
                                <common-icon name="location" size="small"></common-icon>
                                <span class="availability-appointment-modal__sr-only">
                                    {{ $t('pages.availability.index.modal.address') }}
                                </span>
                                <p>{{ address }}</p>
                            </div>
                        </div>
                    </section>

                    <section class="availability-appointment-modal__section availability-appointment-modal__section--flat">
                        <div class="availability-appointment-modal__section-header">
                            <h3>{{ $t('pages.availability.index.modal.medical_information') }}</h3>
                        </div>

                        <div class="availability-appointment-modal__medical-list">
                            <div class="availability-appointment-modal__medical-item">
                                <span>{{ $t('pages.availability.index.modal.insurance_provider') }}</span>
                                <p>{{ insuranceProvider }}</p>
                            </div>

                            <div class="availability-appointment-modal__medical-item">
                                <span>{{ $t('pages.availability.index.modal.reason_for_visit') }}</span>
                                <p>{{ reasonForVisit }}</p>
                            </div>

                            <div class="availability-appointment-modal__medical-item">
                                <span>{{ $t('pages.availability.index.modal.last_visit') }}</span>
                                <p>{{ lastVisitDisplay }}</p>
                            </div>
                        </div>
                    </section>

                    <div class="availability-appointment-modal__footer-divider"></div>

                    <div class="availability-appointment-modal__footer">
                        <button
                            class="availability-appointment-modal__action availability-appointment-modal__action--close"
                            type="button"
                            @click="closeModal"
                        >
                            {{ $t('pages.availability.index.modal.close') }}
                        </button>
                        <button
                            class="availability-appointment-modal__action availability-appointment-modal__action--edit"
                            type="button"
                            @click="editAppointment"
                        >
                            {{ $t('pages.availability.index.modal.edit_appointment') }}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </portal>
</template>

<script>
    import dayjs from 'dayjs'
    import ReferralsAPI from 'api/ReferralsAPI'

    const CONFIRMED_STATUSES = ['confirmed', 'scheduled', 'completed']
    const PENDING_STATUSES = ['pending']
    const CANCELLED_STATUSES = ['cancelled', 'no-show', 'no_show']

    export default {
        props: {
            appointment: { type: Object, default: null },
            isOpen: { type: Boolean, default: false },
        },

        data() {
            return {
                fetchedReferral: null,
                isLoading: false,
            }
        },

        computed: {
            referral() {
                return this.fetchedReferral || this.appointment?.referral || {}
            },

            patient() {
                return this.resolvePatient(this.referral)
            },

            patientName() {
                const patientName = this.composeDisplayName(this.patient)
                return (
                    patientName ||
                    this.composeDisplayName(this.referral) ||
                    this.$t('pages.availability.index.modal.fallbacks.unknown_patient')
                )
            },

            patientInitials() {
                const parts = this.patientName.split(' ').filter(Boolean)
                if (!parts.length) {
                    return 'P'
                }

                return parts
                    .slice(0, 2)
                    .map((part) => part[0])
                    .join('')
                    .toUpperCase()
            },

            ageText() {
                const age = this.resolveAge(this.patient)
                if (age === null) {
                    return this.$t('pages.availability.index.modal.fallbacks.unknown_age')
                }

                return `${this.$t('pages.availability.index.modal.age')} ${age}`
            },

            appointmentDate() {
                if (!this.appointment?.date) {
                    return this.$t('pages.availability.index.modal.fallbacks.not_available')
                }

                return dayjs(this.appointment.date).format('MMMM D, YYYY')
            },

            appointmentTime() {
                if (!this.appointment?.startTime || !this.appointment?.endTime) {
                    return this.$t('pages.availability.index.modal.fallbacks.not_available')
                }

                return [this.appointment.startTime, this.appointment.endTime].join(' - ')
            },

            appointmentType() {
                return (
                    this.referral.appointmentType ||
                    this.referral.type ||
                    this.referral.visitType ||
                    this.referral.category ||
                    this.$t('pages.availability.index.modal.fallbacks.not_available')
                )
            },

            appointmentStatusLabel() {
                if (this.referral.status && CONFIRMED_STATUSES.includes(this.referral.status)) {
                    return this.$t('pages.availability.index.statuses.confirmed')
                }

                if (this.referral.status && PENDING_STATUSES.includes(this.referral.status)) {
                    return this.$t('pages.availability.index.statuses.pending')
                }

                if (this.referral.status && CANCELLED_STATUSES.includes(this.referral.status)) {
                    return this.$t('pages.availability.index.statuses.cancelled')
                }

                return this.$t('pages.availability.index.statuses.confirmed')
            },

            appointmentStatusClass() {
                const status = this.referral.status

                return {
                    'availability-appointment-modal__status': true,
                    'availability-appointment-modal__status--confirmed': !status || CONFIRMED_STATUSES.includes(status),
                    'availability-appointment-modal__status--pending': PENDING_STATUSES.includes(status),
                    'availability-appointment-modal__status--cancelled': CANCELLED_STATUSES.includes(status),
                }
            },

            phone() {
                return (
                    this.patient.phone ||
                    this.patient.mobilePhone ||
                    this.patient.mobile ||
                    this.referral.phone ||
                    this.referral.contactPhone ||
                    this.$t('pages.availability.index.modal.fallbacks.not_available')
                )
            },

            email() {
                return (
                    this.patient.email ||
                    this.referral.email ||
                    this.referral.contactEmail ||
                    this.$t('pages.availability.index.modal.fallbacks.not_available')
                )
            },

            address() {
                const patient = this.patient
                const addressParts = []

                if (typeof patient.address === 'string' && patient.address) {
                    if (patient.address) addressParts.push(patient.address)
                    const cityState = [patient.city, patient.state].filter(Boolean).join(', ')
                    if (cityState) addressParts.push(cityState)
                    if (patient.zip) addressParts.push(patient.zip)
                    return addressParts.length
                        ? addressParts.join(', ')
                        : this.$t('pages.availability.index.modal.fallbacks.not_available')
                }

                const patientAddress = patient.address || this.referral.address || {}
                if (typeof patientAddress === 'string')
                    return patientAddress || this.$t('pages.availability.index.modal.fallbacks.not_available')

                const line1 =
                    patientAddress.line1 ||
                    patientAddress.address1 ||
                    patientAddress.address ||
                    patientAddress.street ||
                    patientAddress.street1
                const line2 = patientAddress.line2 || patientAddress.address2 || patientAddress.street2
                const city = patientAddress.city
                const state = patientAddress.state
                const zip = patientAddress.zip || patientAddress.zipCode || patientAddress.postalCode

                if (line1) addressParts.push(line1)
                if (line2) addressParts.push(line2)

                const cityState = [city, state].filter(Boolean).join(', ')
                if (cityState) addressParts.push(cityState)
                if (zip) addressParts.push(zip)

                return addressParts.length
                    ? addressParts.join(', ')
                    : this.$t('pages.availability.index.modal.fallbacks.not_available')
            },

            insuranceProvider() {
                const plan = this.referral.insurancePlan
                if (plan && typeof plan === 'object')
                    return plan.name || this.$t('pages.availability.index.modal.fallbacks.not_available')
                return (
                    plan ||
                    this.patient.insuranceProvider ||
                    this.patient.insurancePlan ||
                    this.referral.insuranceProvider ||
                    this.referral.insurance?.provider ||
                    this.$t('pages.availability.index.modal.fallbacks.not_available')
                )
            },

            reasonForVisit() {
                return (
                    this.referral.diagnosis ||
                    this.referral.reasonForVisit ||
                    this.referral.reason ||
                    this.referral.notes ||
                    this.referral.summary ||
                    this.$t('pages.availability.index.modal.fallbacks.not_available')
                )
            },

            lastVisit() {
                const lastVisitSource =
                    this.patient.lastVisit || this.patient.lastVisitDate || this.referral.lastVisit || this.referral.lastVisitDate

                if (!lastVisitSource) {
                    return ''
                }

                if (typeof lastVisitSource === 'string' && lastVisitSource.includes('-')) {
                    const parsed = dayjs(lastVisitSource)
                    if (parsed.isValid()) {
                        return parsed.format('MMMM D, YYYY')
                    }
                }

                return lastVisitSource
            },

            lastVisitDisplay() {
                return this.lastVisit || this.$t('pages.availability.index.modal.fallbacks.not_available')
            },
        },

        methods: {
            closeModal() {
                this.$emit('close')
            },

            editAppointment() {
                const referralId = this.referral?.id || this.appointment?.referral?.id
                if (!referralId) return
                this.$emit('edit-appointment', referralId)
            },

            fetchReferralDetails(referralId) {
                if (!referralId) return
                this.isLoading = true
                this.fetchedReferral = null
                const physicianId = this.$auth.getActiveMember()?.id || this.$auth.getUser()?.id
                ReferralsAPI.get(referralId, physicianId)
                    .then((referral) => {
                        this.fetchedReferral = referral
                    })
                    .catch(() => {})
                    .finally(() => {
                        this.isLoading = false
                    })
            },

            composeDisplayName(user) {
                if (!user || typeof user !== 'object') {
                    return ''
                }

                const pieces = []
                if (user.firstName) pieces.push(user.firstName)
                if (user.middleName) pieces.push(user.middleName)
                if (user.lastName) pieces.push(user.lastName)

                if (pieces.length) {
                    return pieces.join(' ').trim()
                }

                return user.name || user.patientName || ''
            },

            resolvePatient(referral) {
                if (!referral) {
                    return {}
                }

                if (referral.patient && typeof referral.patient === 'object') {
                    return referral.patient
                }

                if (referral.referralInfo?.patient && typeof referral.referralInfo.patient === 'object') {
                    return referral.referralInfo.patient
                }

                if (referral.patientInfo && typeof referral.patientInfo === 'object') {
                    return referral.patientInfo
                }

                if (referral.patientData && typeof referral.patientData === 'object') {
                    return referral.patientData
                }

                return referral
            },

            resolveAge(patient) {
                if (!patient) {
                    return null
                }

                if (Number.isFinite(patient.age)) {
                    return patient.age
                }

                const dateOfBirth = patient.dateOfBirth || patient.dob || patient.birthDate
                if (!dateOfBirth) {
                    return null
                }

                const birthDate = dayjs(dateOfBirth)
                if (!birthDate.isValid()) {
                    return null
                }

                const age = dayjs().diff(birthDate, 'year')
                return age >= 0 ? age : null
            },
        },

        watch: {
            isOpen: {
                immediate: true,
                handler(isOpen) {
                    if (typeof document === 'undefined') return
                    document.body.classList.toggle('no-scroll', !!isOpen)
                },
            },

            appointment: {
                immediate: true,
                handler(appointment) {
                    this.fetchedReferral = null
                    const referralId = appointment?.referral?.id
                    if (referralId) {
                        this.fetchReferralDetails(referralId)
                    }
                },
            },
        },

        beforeDestroy() {
            if (typeof document !== 'undefined') {
                document.body.classList.remove('no-scroll')
            }
        },
    }
</script>

<style scoped>
    .availability-appointment-modal__overlay {
        position: fixed;
        inset: 0;
        z-index: 1000;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 16px;
        background: rgba(0, 0, 0, 0.86);
        overflow: hidden;
    }

    .availability-appointment-modal {
        position: relative;
        display: flex;
        flex-direction: column;
        width: min(700px, calc(100vw - 32px));
        max-height: 84vh;
        background: #ffffff;
        border-radius: 12px 0 0 12px;
        box-shadow: 0 28px 80px rgba(0, 0, 0, 0.28);
        overflow-y: auto;
        overflow-x: hidden;
        scrollbar-width: thin;
        scrollbar-color: rgba(148, 163, 184, 0.9) transparent;
    }

    .availability-appointment-modal::-webkit-scrollbar {
        width: 11px;
    }

    .availability-appointment-modal::-webkit-scrollbar-track {
        background: transparent;
    }

    .availability-appointment-modal::-webkit-scrollbar-thumb {
        background: rgba(148, 163, 184, 0.9);
        border-radius: 999px;
        border: 2px solid transparent;
        background-clip: content-box;
    }

    .availability-appointment-modal__close {
        position: absolute;
        top: 16px;
        right: 16px;
        width: 38px;
        height: 38px;
        border-radius: 10px;
        color: #9ca3af;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: background 0.15s ease, color 0.15s ease, transform 0.15s ease;
        z-index: 2;
    }

    .availability-appointment-modal__close:hover {
        background: #eef2f7;
        color: #6b7280;
    }

    .availability-appointment-modal__close-icon {
        width: 14px;
        height: 14px;
    }

    .availability-appointment-modal__header {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 20px;
        padding: 18px 28px 14px;
        padding-right: 68px;
    }

    .availability-appointment-modal__identity {
        display: flex;
        align-items: center;
        gap: 14px;
        min-width: 0;
    }

    .availability-appointment-modal__avatar {
        width: 56px;
        height: 56px;
        border-radius: 50%;
        background: #dbeafe;
        color: #2563eb;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 17px;
        font-weight: 700;
        letter-spacing: 0.08em;
        flex-shrink: 0;
    }

    .availability-appointment-modal__identity-copy {
        min-width: 0;
    }

    .availability-appointment-modal__patient-name {
        font-size: 20px;
        line-height: 1.18;
        font-weight: 500;
        color: #111827;
        margin: 0;
    }

    .availability-appointment-modal__patient-age {
        margin: 4px 0 0;
        font-size: 15px;
        line-height: 1.35;
        color: #6b7280;
    }

    .availability-appointment-modal__status {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        padding: 6px 12px;
        border-radius: 999px;
        font-size: 11px;
        font-weight: 700;
        letter-spacing: 0.04em;
        text-transform: uppercase;
        flex-shrink: 0;
        margin-top: 0;
    }

    .availability-appointment-modal__status--confirmed {
        background: #dcfce7;
        color: #15803d;
    }

    .availability-appointment-modal__status--pending {
        background: #ffedd5;
        color: #c2410c;
    }

    .availability-appointment-modal__status--cancelled {
        background: #fee2e2;
        color: #b91c1c;
    }

    .availability-appointment-modal__divider {
        height: 1px;
        background: #e5e7eb;
        margin: 0 0 18px;
        flex-shrink: 0;
    }

    .availability-appointment-modal__content {
        display: flex;
        flex: 1;
        flex-direction: column;
        gap: 18px;
        overflow: visible;
        padding: 0 28px 18px;
    }

    .availability-appointment-modal__section {
        border-radius: 13px;
        background: #ffffff;
        border: 1px solid #e5e7eb;
        padding: 14px 22px;
    }

    .availability-appointment-modal__section--flat {
        border: 0;
        background: transparent;
        padding: 0;
        border-radius: 0;
    }

    .availability-appointment-modal__section--details {
        background: #f8fafc;
        border-color: #edf2f7;
    }

    .availability-appointment-modal__section--flat .availability-appointment-modal__section-header {
        margin-bottom: 14px;
    }

    .availability-appointment-modal__section--flat .availability-appointment-modal__section-header h3 {
        font-size: 15px;
        line-height: 1.25;
        font-weight: 500;
    }

    .availability-appointment-modal__section--details .availability-appointment-modal__section-header h3 {
        font-size: 14px;
        line-height: 1.2;
        font-weight: 500;
    }

    .availability-appointment-modal__section--details .availability-appointment-modal__status {
        padding: 4px 11px;
        border-radius: 8px;
        border: 1px solid #22c55e;
        background: #22c55e;
        color: #ffffff;
        font-size: 10px;
        font-weight: 600;
        letter-spacing: 0.01em;
        text-transform: none;
    }

    .availability-appointment-modal__section-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        margin-bottom: 10px;
    }

    .availability-appointment-modal__section-header h3 {
        font-size: 16px;
        line-height: 1.25;
        font-weight: 600;
        color: #111827;
        margin: 0;
    }

    .availability-appointment-modal__detail-grid {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 14px 18px;
    }

    .availability-appointment-modal__detail-item {
        display: flex;
        align-items: flex-start;
        gap: 10px;
        min-width: 0;
    }

    .availability-appointment-modal__detail-item--wide {
        grid-column: 1 / -1;
        max-width: 50%;
    }

    .availability-appointment-modal__detail-copy {
        min-width: 0;
    }

    .availability-appointment-modal__detail-copy p,
    .availability-appointment-modal__list-item p,
    .availability-appointment-modal__medical-item p {
        font-size: 12px;
        line-height: 1.35;
        color: #111827;
        font-weight: 400;
        margin: 0;
        word-break: break-word;
    }

    .availability-appointment-modal__sr-only {
        position: absolute;
        width: 1px;
        height: 1px;
        padding: 0;
        margin: -1px;
        overflow: hidden;
        clip: rect(0, 0, 0, 0);
        white-space: nowrap;
        border: 0;
    }

    .availability-appointment-modal__list {
        display: flex;
        flex-direction: column;
        gap: 14px;
    }

    .availability-appointment-modal__list-item,
    .availability-appointment-modal__medical-item {
        display: flex;
        align-items: flex-start;
        gap: 12px;
        min-width: 0;
    }

    .availability-appointment-modal__list-item p {
        font-size: 13px;
        line-height: 1.35;
    }

    .availability-appointment-modal__list-item .availability-appointment-modal__sr-only,
    .availability-appointment-modal__medical-item span {
        display: block;
        font-size: 10px;
        line-height: 1.35;
        color: #6b7280;
        margin-bottom: 3px;
        font-weight: 400;
    }

    .availability-appointment-modal__list-item--address p {
        max-width: 44rem;
    }

    .availability-appointment-modal__medical-list {
        display: flex;
        flex-direction: column;
        gap: 18px;
    }

    .availability-appointment-modal__medical-item {
        display: flex;
        flex-direction: column;
        gap: 2px;
    }

    .availability-appointment-modal__medical-item span {
        margin-bottom: 0;
        font-size: 12px;
        line-height: 1.25;
        color: #6b7280;
        font-weight: 400;
    }

    .availability-appointment-modal__medical-item p {
        font-size: 13px;
        line-height: 1.35;
    }

    .availability-appointment-modal__footer {
        display: flex;
        justify-content: flex-end;
        align-items: center;
        gap: 14px;
        padding: 6px 0 0;
    }

    .availability-appointment-modal__footer-divider {
        height: 1px;
        background: #e5e7eb;
        margin-top: 4px;
    }

    .availability-appointment-modal__action {
        min-height: 42px;
        padding: 0 20px;
        border-radius: 12px;
        font-size: 12px;
        line-height: 1.2;
        letter-spacing: 0.01em;
        font-weight: 500;
        transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
    }

    .availability-appointment-modal__action--close {
        background: #ffffff;
        border: 1px solid #d1d5db;
        color: #111827;
    }

    .availability-appointment-modal__action--close:hover {
        background: #f9fafb;
    }

    .availability-appointment-modal__action--edit {
        background: #0f172a;
        border: 1px solid #0f172a;
        color: #ffffff;
    }

    .availability-appointment-modal__action--edit:hover {
        background: #020617;
        border-color: #020617;
    }

    .availability-appointment-modal__loading {
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 16px 0 8px;
    }

    .availability-appointment-modal__spinner {
        display: inline-block;
        width: 22px;
        height: 22px;
        border: 2px solid #e5e7eb;
        border-top-color: #3b82f6;
        border-radius: 50%;
        animation: availability-spin 0.7s linear infinite;
    }

    @keyframes availability-spin {
        to {
            transform: rotate(360deg);
        }
    }

    @media (max-width: 900px) {
        .availability-appointment-modal {
            width: min(640px, calc(100vw - 24px));
            max-height: 82vh;
        }

        .availability-appointment-modal__detail-item--wide,
        .availability-appointment-modal__medical-item:nth-child(3) {
            max-width: none;
        }
    }

    @media (max-width: 768px) {
        .availability-appointment-modal__overlay {
            padding: 12px;
        }

        .availability-appointment-modal {
            width: calc(100vw - 24px);
            max-height: 86vh;
        }

        .availability-appointment-modal__header,
        .availability-appointment-modal__content,
        .availability-appointment-modal__footer {
            padding-left: 14px;
            padding-right: 14px;
        }

        .availability-appointment-modal__header {
            padding-top: 16px;
            padding-bottom: 12px;
            padding-right: 54px;
        }

        .availability-appointment-modal__detail-grid {
            grid-template-columns: 1fr;
        }

        .availability-appointment-modal__detail-item--wide {
            grid-column: auto;
            max-width: none;
        }
    }

    @media (max-width: 600px) {
        .availability-appointment-modal__avatar {
            width: 48px;
            height: 48px;
            font-size: 15px;
        }

        .availability-appointment-modal__patient-name {
            font-size: 18px;
        }

        .availability-appointment-modal__patient-age {
            font-size: 13px;
        }

        .availability-appointment-modal__section {
            padding: 14px;
            border-radius: 12px;
        }

        .availability-appointment-modal__section--flat {
            padding: 0;
            border-radius: 0;
        }

        .availability-appointment-modal__footer {
            width: 100%;
            justify-content: flex-end;
        }

        .availability-appointment-modal__action {
            min-width: auto;
        }
    }
</style>
